import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import './SignatureButton.css';

/**
 * One signature button used across the site so CTAs stop looking like a UI-kit default.
 * - `to`   -> react-router Link
 * - `href` -> anchor
 * - else   -> native button (supports type="submit")
 * Adds a subtle magnetic pull toward the cursor + a sheen sweep + arrow motion.
 */
const SignatureButton = ({
  children,
  variant = 'primary',
  withArrow = true,
  className = '',
  to,
  href,
  type = 'button',
  magnetic = true,
  ...rest
}) => {
  const innerRef = useRef(null);

  const handlePointerMove = (e) => {
    if (!magnetic) return;
    const el = e.currentTarget;
    const inner = innerRef.current;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    el.style.transform = `translate(${x * 0.18}px, ${y * 0.28}px)`;
    if (inner) inner.style.transform = `translate(${x * 0.08}px, ${y * 0.12}px)`;
  };

  const resetTransform = (e) => {
    e.currentTarget.style.transform = '';
    if (innerRef.current) innerRef.current.style.transform = '';
  };

  const content = (
    <span className="sigBtn-inner" ref={innerRef}>
      <span className="sigBtn-label">{children}</span>
      {withArrow && (
        <svg
          className="sigBtn-arrow"
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      )}
    </span>
  );

  const sharedProps = {
    className: `sigBtn sigBtn--${variant} ${className}`.trim(),
    onPointerMove: handlePointerMove,
    onPointerLeave: resetTransform,
    onBlur: resetTransform,
    ...rest,
  };

  if (to) {
    return (
      <Link to={to} {...sharedProps}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} {...sharedProps}>
        {content}
      </a>
    );
  }

  return (
    <button type={type} {...sharedProps}>
      {content}
    </button>
  );
};

export default SignatureButton;
